/**
 * EventChip — a single calendar event row with color coding,
 * left accent bar, inline status, relative time, and overdue indicators.
 * Recurring = blue, One-time = green, Google = red, Completed = gray.
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import Animated, { Easing, FadeInDown } from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';
import { useTheme, ThemeColors } from '../../utils/theme';
import type { CalendarEvent } from '../../types/api';

// Google brand blue — the synced indicator across every calendar view.
const GOOGLE_BLUE = '#4285F4';
import { formatRelativeTime, formatTime, formatRecurrenceRule } from '../../utils/localization';
import { getPrimaryTagColor } from '../../utils/tagColors';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';

interface Props {
  event: CalendarEvent;
  onPress?: () => void;
  tagColors?: Record<string, string> | null;
  index?: number;
  animateEntrance?: boolean;
}

const EVENT_COLORS = {
  recurring: '#3478F6', // blue
  oneTime: '#34C759', // green
  google: '#EA4335', // red
  done: '#8E8E93', // gray
  paused: '#FF9500', // orange
};

const statusBadgeBgCache: Record<string, { backgroundColor: string }> = {};
function getStatusBadgeBg(color: string) {
  if (!statusBadgeBgCache[color]) {
    statusBadgeBgCache[color] = { backgroundColor: color + '20' };
  }
  return statusBadgeBgCache[color];
}

const statusTextColorCache: Record<string, { color: string }> = {};
function getStatusTextColor(color: string) {
  if (!statusTextColorCache[color]) {
    statusTextColorCache[color] = { color };
  }
  return statusTextColorCache[color];
}

function getEventColor(event: CalendarEvent, tagColorOverrides?: Record<string, string> | null): string {
  if (event.type === 'reminder' && event.tags) {
    const tagColor = getPrimaryTagColor(event.tags, tagColorOverrides);
    if (tagColor) return tagColor;
  }
  if (event.type === 'google') return EVENT_COLORS.google;
  if (event.sent) return EVENT_COLORS.done;
  if (event.isPaused) return EVENT_COLORS.paused;
  if (event.isRecurring) return EVENT_COLORS.recurring;
  return EVENT_COLORS.oneTime;
}

function getStatusLabel(event: CalendarEvent, t: (key: string) => string, colors: ThemeColors): { text: string; color: string } | null {
  if (event.sent) return { text: t('calendar.done'), color: EVENT_COLORS.done };
  if (event.isPaused) return { text: t('calendar.paused'), color: EVENT_COLORS.paused };
  // Check if overdue
  if (event.runAtUtc && new Date(event.runAtUtc) < new Date()) {
    return { text: t('reminders.overdue'), color: colors.error };
  }
  return { text: t('calendar.active'), color: EVENT_COLORS.oneTime };
}

function EventChip({ event, onPress, tagColors, index = 0, animateEntrance = true }: Props) {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const time = event.runAtUtc ? formatTime(event.runAtUtc, i18n.language) : null;

  const color = getEventColor(event, tagColors);
  const statusLabel = getStatusLabel(event, t, colors);
  const relTime = event.runAtUtc ? formatRelativeTime(event.runAtUtc, i18n.language) : null;

  const handlePress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (onPress) onPress();
  };

  const Wrapper = onPress ? TouchableOpacity : View;
  const wrapperProps = onPress ? { onPress: handlePress, activeOpacity: 0.7 } : {};

  return (
    <Animated.View
      entering={animateEntrance
        ? FadeInDown.delay(Math.min(index, 6) * 24).duration(180).easing(Easing.out(Easing.cubic))
        : undefined}
    >
      <Wrapper style={s.touchWrap} {...(wrapperProps as any)}>
        {/* Left accent bar replaces full-width softBg */}
        <View style={[s.accentBar, { backgroundColor: color }]} />
        <View style={s.content}>
          <View style={s.container}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={[s.title, event.sent && s.titleDone, { flexShrink: 1 }]} numberOfLines={2}>
                {event.body}
              </Text>
              {event.source === 'google' ? (
                <Ionicons name="logo-google" size={11} color="#EA4335" accessibilityLabel="From Google Calendar" />
              ) : event.googleEventId ? (
                <Ionicons name="logo-google" size={11} color={GOOGLE_BLUE} accessibilityLabel="Synced to Google Calendar" />
              ) : null}
            </View>
            <View style={s.metaRow}>
              {time && <View style={s.timeChip}><Text style={s.time}>{time}</Text></View>}
              {relTime && <Text style={[s.relTime, event.sent && s.relTimeDone]}>{relTime}</Text>}
              {event.isRecurring && !event.sent && <Text style={s.recurBadge} numberOfLines={1}>{formatRecurrenceRule(event.recurJson, i18n.language, event.untilUtc) || t('reminder.recurring')}</Text>}
              {statusLabel && (
                <View style={[s.statusTag, getStatusBadgeBg(statusLabel.color)]}>
                  <Text style={[s.statusText, getStatusTextColor(statusLabel.color)]}>{statusLabel.text}</Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Wrapper>
    </Animated.View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  touchWrap: {
    marginHorizontal: SPACING.base,
    marginVertical: SPACING.sm,
    borderRadius: RADIUS.xl,
    backgroundColor: colors.card,
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    ...SHADOW.card,
    ...(Platform.OS === 'ios' ? {
      shadowOffset: { width: 0, height: 6 },
    } : {}),
  },
  accentBar: {
    width: SPACING.xs,
    borderTopLeftRadius: RADIUS.xl,
    borderBottomLeftRadius: RADIUS.xl,
  },
  container: {
    flex: 1,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    zIndex: 1,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  title: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    lineHeight: 20,
    color: colors.text,
    letterSpacing: 0.1,
  },
  titleDone: {
    textDecorationLine: 'line-through',
    color: colors.textSecondary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.sm,
  },
  timeChip: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.xs,
    borderRadius: RADIUS.full,
    backgroundColor: colors.surfaceTinted,
  },
  time: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  relTime: {
    fontSize: FONT_SIZE.xs,
    color: colors.textDim,
    fontWeight: '500',
  },
  relTimeDone: {
    color: colors.textSecondary,
  },
  recurBadge: {
    fontSize: FONT_SIZE.xs,
    color: '#3478F6',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  statusTag: {
    paddingHorizontal: SPACING.sm,
    paddingVertical: 3,
    borderRadius: RADIUS.full,
  },
  statusText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
  },
});

export default React.memo(EventChip, (prevProps, nextProps) => {
  return (
    prevProps.event.seq === nextProps.event.seq &&
    prevProps.event.runAtUtc === nextProps.event.runAtUtc &&
    prevProps.event.isPaused === nextProps.event.isPaused &&
    prevProps.event.sent === nextProps.event.sent &&
    prevProps.event.body === nextProps.event.body &&
    prevProps.event.googleEventId === nextProps.event.googleEventId &&
    prevProps.tagColors === nextProps.tagColors &&
    prevProps.index === nextProps.index &&
    prevProps.animateEntrance === nextProps.animateEntrance
  );
});
